import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { SwitchWithLabel } from "@volca/ui/components/ui/switch-with-label";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { SectionHeading } from "@/accounts-payable/common";
import type { QuickbooksConnectionDetails } from "@/business/quickbooks";
import {
  initialQuickbooksBillSettings,
  isQuickbooksSyncing,
} from "@/business/quickbooks-utils";
import { QuickbooksDefaultAccountField } from "./quickbooks-default-account-field";
import { QuickbooksStartDateField } from "./quickbooks-start-date-field";
import type { BillSettingsDraft } from "./use-bill-settings-draft";

/**
 * The bill settings, edited in place in the QuickBooks view. Untouched fields
 * follow the connection as it refetches, so the suggested account appears once
 * the mirror has synced; a field the user changed keeps their value.
 */
export const QuickbooksBillSettingsSection = ({
  businessId,
  connection,
  settings,
  onReviewVendors,
}: {
  businessId: string;
  connection: QuickbooksConnectionDetails;
  settings: BillSettingsDraft;
  /** Switches AP to the Vendors view. */
  onReviewVendors: () => void;
}) => {
  const { draft, change, error, save, isSaving } = settings;
  const values = { ...initialQuickbooksBillSettings(connection), ...draft };
  const isSyncing = isQuickbooksSyncing(connection);
  const { linked, total } = connection.vendor_links;

  return (
    <Stack className="w-full" align="start" gap={6}>
      <Stack className="w-full" align="start" gap={1}>
        <SectionHeading>Bill settings</SectionHeading>
        <Text tone="muted">
          Choose where bills from your AP invoices are filed in{" "}
          {connection.company_name ?? "QuickBooks"} and when they start.
        </Text>
      </Stack>

      <QuickbooksDefaultAccountField
        businessId={businessId}
        isSyncing={isSyncing}
        value={values.default_expense_account_id}
        onChange={(id) => change({ default_expense_account_id: id })}
        disabled={isSaving}
      />

      <QuickbooksStartDateField
        value={values.bills_start_date}
        onChange={(date) => change({ bills_start_date: date })}
        disabled={isSaving}
      />

      <Stack className="w-full" align="start" gap={1}>
        <Text variant="label" tone="muted">
          Vendors
        </Text>
        {!isSyncing && total === 0 ? (
          <Empty className={emptyStateVariants({ layout: "inline" })}>
            <EmptyHeader>
              <EmptyDescription>No AP vendors yet.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Text>
            {isSyncing
              ? "Vendors are matched once your QuickBooks data finishes loading."
              : `${linked} of ${total} vendors are matched to QuickBooks vendors.`}
          </Text>
        )}
        {!isSyncing && linked < total && (
          <Button variant="link" size="sm" onClick={onReviewVendors}>
            Review vendors
          </Button>
        )}
      </Stack>

      <SwitchWithLabel
        label="Create bills in QuickBooks"
        aria-label="Create bills in QuickBooks"
        checked={values.push_bills_enabled}
        onChange={(checked) => change({ push_bills_enabled: checked })}
        disabled={isSaving}
      />

      {error && (
        <Text tone="destructive" role="alert">
          {error}
        </Text>
      )}

      <Button
        type="button"
        size="sm"
        className="w-fit"
        onClick={() => save(values)}
        disabled={isSaving}
        aria-busy={isSaving || undefined}
      >
        Save bill settings
      </Button>
    </Stack>
  );
};
